import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Button from "../components/Button";
import Section from "../components/Section";
import Seo from "../components/Seo";
import CountUp from "../components/CountUp";
import FloatingNotes from "../components/FloatingNotes";
import CtaBlobs from "../components/CtaBlobs";
import {
  fadeUp,
  springSoft,
  staggerContainer,
  viewportOnce,
} from "../lib/motion";
import {
  Baby,
  Ear,
  Footprints,
  HeartHandshake,
  Mic,
  Music,
  Music4,
  Quote,
  Salad,
  Star,
  Trees,
  Users,
} from "lucide-react";
import { organizationJsonLd, websiteJsonLd } from "../lib/jsonld";
import { PREINSCRIPTION_URL } from "../data/site";

export default function Home() {
  return (
    <>
      <Seo
        path="/"
        title="Micro-crèches en Normandie"
        description="Nos micro-crèches La La Land en Normandie. La musique au cœur du quotidien de vos enfants de 10 semaines à 5 ans. 14 enfants maximum par structure, équipe en or."
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />
      <section className="relative w-full h-[68vh] md:h-[80vh] min-h-[480px] md:min-h-[520px] max-h-[760px] overflow-hidden">
        <video
          src="/images/hero-video.mp4"
          poster="/images/hero-video-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent md:hidden" />
        <FloatingNotes />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-8 flex items-center">
          <motion.div
            className="max-w-2xl text-white"
            variants={staggerContainer(0.15, 0.1)}
            initial="hidden"
            animate="show"
          >
            <motion.p
              variants={fadeUp}
              className="text-sm md:text-base font-medium tracking-wide opacity-95 [text-shadow:0_1px_8px_rgba(0,0,0,0.35)]"
            >
              La musique, au cœur de nos activités multi-sensorielles
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="mt-4 font-brand text-5xl md:text-7xl lg:text-8xl font-semibold leading-[0.95] [text-shadow:0_2px_24px_rgba(0,0,0,0.25)]"
            >
              Micro-crèches
              <br />
              LA LA LAND
            </motion.h1>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
              <a
                href={PREINSCRIPTION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="primary" size="lg">
                  Pré-inscription
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
        <svg
          className="absolute bottom-0 left-0 w-full h-16 md:h-24"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,60 C240,120 480,0 720,40 C960,80 1200,20 1440,60 L1440,120 L0,120 Z"
            fill="#5fb3a3"
          />
        </svg>
      </section>

      <Section
        eyebrow="Avis des parents"
        title="Ce que disent les familles"
      >
        <motion.ul
          className="-mx-4 px-4 flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:px-0 md:grid md:grid-cols-2 md:gap-5 md:overflow-visible md:pb-0"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {[
            {
              author: "Anne Catherine",
              when: "il y a 3 mois",
              text: "Mon fils de 2 ans et demi va à la micro-crèche La La Land Maestro à Notre-Dame-de-Bondeville depuis un an. Son papa et moi-même sommes très contents du travail de Cindy, Marie, Inès et l'ensemble de l'équipe qui s'occupent des enfants avec beaucoup de bienveillance et d'investissement. Les activités sont variées, l'équipe est à l'écoute… nous sommes vraiment ravis d'avoir trouvé cette structure !",
              tone: "bg-sage-soft",
            },
            {
              author: "Noémie Cacheux",
              when: "il y a 3 mois",
              text: "Notre fils va à la crèche de Notre-Dame-de-Bondeville depuis ses deux mois et demi et c'est un vrai bonheur de le savoir en sécurité avec une équipe au top qui ne lésine pas sur les câlins et la bienveillance pour l'accompagner au quotidien. Merci aux filles de faire de nous des parents sereins quand il est là-bas.",
              tone: "bg-honey-soft",
            },
            {
              author: "Morgane Mullet",
              when: "il y a 3 mois",
              text: "Personnel très compétent, à l'écoute de nos enfants. Nous recommandons vivement cette micro-crèche conviviale. Que ce soit Marie, Inès ou Cindy, que de professionnelles pour laisser son enfant en toute sécurité.",
              tone: "bg-terra-soft",
            },
            {
              author: "Julie Thomas",
              when: "il y a 3 mois",
              text: "Super micro-crèche Maestro ! Notre grand y a été et maintenant c'est notre fille qui y est ! L'équipe de Cindy est au top ! Je recommande +++",
              tone: "bg-muted",
            },
          ].map((review) => (
            <motion.li
              key={review.author}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className={`${review.tone} relative snap-start shrink-0 w-[86%] sm:w-[70%] md:w-auto rounded-3xl p-6 shadow-soft-sm flex flex-col transition-shadow hover:shadow-soft-md`}
            >
              <Quote
                aria-hidden="true"
                className="absolute top-5 right-5 w-9 h-9 text-ink/10"
                strokeWidth={1.5}
              />
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white shadow-soft-sm flex items-center justify-center font-display text-ink font-semibold">
                  {review.author
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <div>
                  <p className="font-medium text-ink leading-tight">
                    {review.author}
                  </p>
                  <p className="text-xs text-ink-soft">{review.when}</p>
                </div>
              </div>
              <p
                className="mt-4 flex items-center gap-1.5"
                aria-label="5 étoiles sur 5"
              >
                <span className="flex gap-0.5" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-terra text-terra"
                      strokeWidth={1.5}
                    />
                  ))}
                </span>
                <span className="text-ink text-xs font-semibold">5 / 5</span>
              </p>
              <p className="mt-4 text-ink/80 leading-relaxed text-[15px]">
                « {review.text} »
              </p>
            </motion.li>
          ))}
        </motion.ul>
        <div className="mt-8 flex justify-center">
          <a
            href="https://maps.google.com/maps?cid=17760215448089637092"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-ink/10 text-ink font-medium shadow-soft-sm hover:bg-sage-soft transition active:scale-[0.97]"
          >
            Voir plus d'avis
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </Section>

      <section className="bg-sage py-8 md:py-12 -mt-px">
        <motion.div
          className="max-w-7xl mx-auto px-4 md:px-8 grid sm:grid-cols-3 gap-6 text-white"
          variants={staggerContainer(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {[
            { value: 14, suffix: "", label: "enfants maximum par structure" },
            { value: 5, suffix: "", label: "professionnel·les par crèche" },
            { value: 10, suffix: " sem.", label: "âge minimum d'accueil" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              variants={fadeUp}
              className="text-center sm:text-left"
            >
              <p className="font-display text-4xl md:text-5xl font-semibold leading-none">
                <CountUp value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="mt-2 text-sm md:text-base opacity-90 leading-snug">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <Section
        eyebrow="Présentation"
        title="Un cocon chaleureux pour votre enfant"
        description="Entre la sécurité d'une structure collective et l'environnement familial d'une assistante maternelle, la micro-crèche La La Land est le meilleur des deux mondes."
      >
        <motion.ul
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-ink"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {[
            { text: "14 enfants maximum par structure", tone: "bg-terra-soft", icon: Baby, ink: "text-terra-hover" },
            { text: "5 professionnel·les par structure", tone: "bg-honey-soft", icon: Users, ink: "text-honey-hover" },
            { text: "Repas locaux et bio livrés chaque jour", tone: "bg-sage-soft", icon: Salad, ink: "text-sage-hover" },
            { text: "Activités musicales hebdomadaires", tone: "bg-terra-soft", icon: Music, ink: "text-terra-hover" },
            { text: "Sorties extérieures régulières", tone: "bg-sage-soft", icon: Trees, ink: "text-sage-hover" },
            { text: "Couches et soins fournis", tone: "bg-honey-soft", icon: HeartHandshake, ink: "text-honey-hover" },
          ].map((item) => (
            <motion.li
              key={item.text}
              variants={fadeUp}
              whileHover={{ y: -5 }}
              transition={springSoft}
              className={`${item.tone} rounded-2xl p-5 shadow-soft-sm transition-shadow hover:shadow-soft-md flex items-center gap-4`}
            >
              <span className="w-11 h-11 shrink-0 rounded-xl bg-white/80 shadow-soft-sm flex items-center justify-center">
                <item.icon className={`w-5 h-5 ${item.ink}`} strokeWidth={1.8} />
              </span>
              <p className="font-medium leading-snug">{item.text}</p>
            </motion.li>
          ))}
        </motion.ul>
      </Section>

      <Section
        eyebrow="Notre pédagogie"
        title="La musique comme moteur de développement"
        description="Notre projet éducatif s'adresse aux enfants de 10 semaines à 5 ans en respectant leur rythme individuel. Nous favorisons l'épanouissement personnel et social, le développement moteur, sensoriel et cognitif, et la créativité par l'expression artistique."
      >
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {[
            {
              tone: "bg-sage-soft",
              title: "Éveil sensoriel",
              icon: Ear,
              ink: "text-sage-hover",
              text: "Ateliers d'écoute, manipulation d'instruments adaptés et exploration des sons du quotidien dès les premiers mois.",
            },
            {
              tone: "bg-honey-soft",
              title: "Rituels musicaux",
              icon: Music4,
              ink: "text-honey-hover",
              text: "Une comptine pour chaque moment de la journée : accueil, repas, sieste, retrouvailles. La musique structure le temps.",
            },
            {
              tone: "bg-terra-soft",
              title: "Intervenants experts",
              icon: Mic,
              ink: "text-terra-hover",
              text: "Une intervenante musicale passe chaque semaine dans chaque crèche pour des séances dédiées avec les enfants.",
            },
            {
              tone: "bg-muted",
              title: "Motricité libre",
              icon: Footprints,
              ink: "text-ink-soft",
              text: "Jeux corporels, danse libre et exploration de l'espace pour développer l'équilibre, la coordination et la confiance.",
            },
          ].map((pillar) => (
            <motion.article
              key={pillar.title}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className={`${pillar.tone} rounded-3xl p-6 shadow-soft-sm transition-shadow hover:shadow-soft-md`}
            >
              <span className="mb-4 w-12 h-12 rounded-2xl bg-white/80 shadow-soft-sm flex items-center justify-center">
                <pillar.icon className={`w-6 h-6 ${pillar.ink}`} strokeWidth={1.7} />
              </span>
              <h3 className="font-display text-xl font-semibold text-ink leading-tight">
                {pillar.title}
              </h3>
              <p className="mt-3 text-ink-soft leading-relaxed">
                {pillar.text}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-sage to-sage-hover text-white rounded-4xl p-10 md:p-16 shadow-soft-lg">
            <CtaBlobs />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">
                Une place pour votre enfant à la rentrée ?
              </h2>
              <p className="mt-4 text-lg opacity-95 max-w-xl">
                Pré-inscrivez-vous en ligne, nous revenons vers vous sous 48h.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={PREINSCRIPTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-terra hover:bg-terra-hover text-white px-6 py-3.5 rounded-xl font-medium shadow-soft-md transition active:scale-[0.97]"
                >
                  Pré-inscription
                </a>
                <Link
                  to="/tarifs"
                  className="bg-white text-sage-hover hover:bg-cream px-6 py-3.5 rounded-xl font-medium shadow-soft-sm transition active:scale-[0.97]"
                >
                  Voir les tarifs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
